import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Input,
  InputNumber,
  List,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Typography
} from "antd";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import {
  createArticle,
  createBooking,
  deleteArticle,
  getArticle,
  linkInventory,
  listProjects,
  searchArticles,
  updateArticle
} from "./api";
import { calcArticle, normalizeSettings, unitCostOf, unitPriceOf } from "./calc";
import {
  PRICE_UNITS,
  bpToPercent,
  centsToEuro,
  decimalInputProps,
  euroToCents,
  formatEuro,
  formatMillicents,
  kindLabels,
  percentToBp
} from "./format";

const EMPTY = {
  kind: "article",
  name: "",
  shortText2: "",
  longText: "",
  articleNumber: "",
  ean: "",
  manufacturer: "",
  manufacturerNumber: "",
  unit: "Stk",
  supplier: null,
  category: "",
  discountGroup: "",
  priceUnit: 1,
  listPrice: 0,
  purchasePrice: 0,
  discount: null,
  fixedPrice: 0,
  labour: { minutes: 0, wageGroup: "" },
  components: [],
  active: true
};

const Field = ({ label, children, span = 12 }) => (
  <Col xs={24} md={span}>
    <div className="mat-muted">{label}</div>
    {children}
  </Col>
);

/** Picks a catalog article for an assembly part (server search as you type). */
const PartSearch = ({ onPick, exclude }) => {
  const { t } = useTranslation("dashboard");
  const [options, setOptions] = useState([]);
  const search = async q => {
    try {
      const res = await searchArticles({ q, pageSize: 20 });
      setOptions(
        res.data.data
          .filter(a => a._id !== exclude && a.kind !== "assembly")
          .map(a => ({
            value: a._id,
            label: `${a.name}${a.articleNumber ? ` (${a.articleNumber})` : ""}`,
            article: a
          }))
      );
    } catch (error) {
      generalErrorAlert(error);
    }
  };
  return (
    <Select
      showSearch
      className="mat-full"
      placeholder={t("Add a part: search the catalog")}
      filterOption={false}
      onSearch={search}
      onFocus={() => search("")}
      options={options}
      value={null}
      onChange={(value, option) => onPick(option.article)}
    />
  );
};

/**
 * Article / labour item / assembly editor with a live price preview (client twin of the server
 * calculation), linked stock items and material bookings onto projects.
 */
const ArticleEditor = ({ meta, articleId, onClose, onSaved }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const isMobile = useIsMobile();
  const kinds = kindLabels(t);
  const [article, setArticle] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  const [booking, setBooking] = useState({ project: null, quantity: 1, inventory: null });
  const isNew = articleId === "new";
  const canEdit = isNew ? meta.permissions.create : meta.permissions.update;
  const settings = useMemo(() => normalizeSettings(meta.settings), [meta.settings]);
  const money = decimalInputProps(lng);

  useEffect(() => {
    if (!articleId) return;
    if (isNew) {
      setArticle(null);
      setForm({ ...EMPTY, labour: { minutes: 0, wageGroup: settings.defaultWageGroup } });
      return;
    }
    getArticle(articleId)
      .then(res => {
        const a = res.data.data;
        setArticle(a);
        setForm({ ...EMPTY, ...a, supplier: a.supplier || null, labour: a.labour || EMPTY.labour });
      })
      .catch(error => {
        generalErrorAlert(error);
        onClose();
      });
  }, [articleId]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setLabour = (key, value) =>
    setForm(prev => ({ ...prev, labour: { ...prev.labour, [key]: value } }));

  // Live preview for articles and labour; assemblies are priced from their parts on the server
  const preview = useMemo(() => {
    if (form.kind === "assembly") return null;
    const suppliers = new Map(meta.suppliers.map(s => [s._id, s]));
    const c = calcArticle(form, { settings, suppliers, articles: new Map() });
    return { calc: c, price: unitPriceOf(c), cost: Math.round(unitCostOf(c) / 1000) };
  }, [form, settings, meta.suppliers]);

  const payload = () => ({
    ...(isNew ? { kind: form.kind } : {}),
    name: form.name,
    shortText2: form.shortText2,
    longText: form.longText,
    articleNumber: form.articleNumber,
    ean: form.ean,
    manufacturer: form.manufacturer,
    manufacturerNumber: form.manufacturerNumber,
    unit: form.unit,
    supplier: form.supplier || null,
    category: form.category,
    discountGroup: form.discountGroup,
    priceUnit: form.priceUnit,
    listPrice: form.listPrice,
    purchasePrice: form.purchasePrice,
    discount: form.discount,
    fixedPrice: form.fixedPrice,
    labour: form.labour,
    components: (form.components || []).map(c => ({ article: c.article, quantity: c.quantity })),
    active: form.active
  });

  const save = async () => {
    setBusy(true);
    try {
      const res = isNew
        ? await createArticle(payload())
        : await updateArticle(articleId, payload());
      Success(t("Article saved"));
      setArticle(res.data.data);
      onSaved();
      onClose();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await deleteArticle(articleId);
      onSaved();
      onClose();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const stock = article?.stock || [];
  const createStock = async () => {
    try {
      const res = await linkInventory(articleId, { create: true });
      setArticle(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };
  const book = async () => {
    try {
      await createBooking({
        article: articleId,
        quantity: booking.quantity,
        project: booking.project,
        inventory: booking.inventory || stock[0]?._id || null
      });
      Success(t("Material booked"));
      const res = await getArticle(articleId);
      setArticle(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const supplierOptions = [
    { value: null, label: t("Own article (no supplier)") },
    ...meta.suppliers.map(s => ({ value: s._id, label: s.name }))
  ];
  const wageOptions = settings.wageGroups.map(g => ({
    value: g.code,
    label: `${g.code} – ${g.name || g.code}`
  }));

  return (
    <Drawer
      rootClassName="materials-page"
      open={Boolean(articleId)}
      onClose={onClose}
      width={isMobile ? "100%" : 680}
      title={isNew ? t("New article") : form.name || t("Article")}
      destroyOnClose
      footer={
        <Space wrap>
          {canEdit ? (
            <Button type="primary" loading={busy} onClick={save}>
              {t("Save")}
            </Button>
          ) : null}
          {!isNew && meta.permissions.delete ? (
            <Popconfirm title={t("Delete this article?")} onConfirm={remove}>
              <Button danger>{t("Delete")}</Button>
            </Popconfirm>
          ) : null}
          <Button onClick={onClose}>{t("Close")}</Button>
        </Space>
      }
    >
      {isNew ? (
        <Segmented
          block
          value={form.kind}
          onChange={value => set("kind", value)}
          options={["article", "labour", "assembly"].map(k => ({ value: k, label: kinds[k] }))}
        />
      ) : null}
      {article?.source === "import" ? (
        <Alert
          className="mat-section"
          type="info"
          showIcon
          message={t(
            "Imported article: the next DATANORM import of this supplier overwrites texts and prices."
          )}
        />
      ) : null}

      <Row gutter={[16, 16]} className="mat-section">
        <Field label={t("Short text (name)")} span={24}>
          <Input
            value={form.name}
            onChange={e => set("name", e.target.value)}
            disabled={!canEdit}
          />
        </Field>
        <Field label={t("Short text 2")} span={24}>
          <Input
            value={form.shortText2}
            onChange={e => set("shortText2", e.target.value)}
            disabled={!canEdit}
          />
        </Field>
        <Field label={t("Article no.")}>
          <Input
            value={form.articleNumber}
            onChange={e => set("articleNumber", e.target.value)}
            disabled={!canEdit}
          />
        </Field>
        <Field label={t("Unit")}>
          <Input
            value={form.unit}
            onChange={e => set("unit", e.target.value)}
            disabled={!canEdit}
          />
        </Field>
        {form.kind === "article" ? (
          <>
            <Field label={t("EAN")}>
              <Input
                value={form.ean}
                inputMode="numeric"
                onChange={e => set("ean", e.target.value)}
                disabled={!canEdit}
              />
            </Field>
            <Field label={t("Supplier")}>
              <Select
                className="mat-full"
                value={form.supplier}
                options={supplierOptions}
                onChange={value => set("supplier", value)}
                disabled={!canEdit}
              />
            </Field>
            <Field label={t("Manufacturer")}>
              <Input
                value={form.manufacturer}
                onChange={e => set("manufacturer", e.target.value)}
                disabled={!canEdit}
              />
            </Field>
            <Field label={t("Manufacturer article no.")}>
              <Input
                value={form.manufacturerNumber}
                onChange={e => set("manufacturerNumber", e.target.value)}
                disabled={!canEdit}
              />
            </Field>
            <Field label={t("Product group")}>
              <Input
                value={form.category}
                onChange={e => set("category", e.target.value)}
                disabled={!canEdit}
              />
            </Field>
            <Field label={t("Discount group")}>
              <Input
                value={form.discountGroup}
                onChange={e => set("discountGroup", e.target.value)}
                disabled={!canEdit}
              />
            </Field>
            <Field label={t("Price per")}>
              <Select
                className="mat-full"
                value={form.priceUnit}
                onChange={value => set("priceUnit", value)}
                options={PRICE_UNITS.map(n => ({ value: n, label: `${n} ${form.unit || ""}` }))}
                disabled={!canEdit}
              />
            </Field>
            <Field label={t("List price (€)")}>
              <InputNumber
                {...money}
                className="mat-full"
                min={0}
                precision={2}
                value={centsToEuro(form.listPrice)}
                onChange={value => set("listPrice", euroToCents(value))}
                disabled={!canEdit}
              />
            </Field>
            <Field label={t("Net purchase price (€, empty = list price minus discount)")}>
              <InputNumber
                {...money}
                className="mat-full"
                min={0}
                precision={2}
                value={form.purchasePrice ? centsToEuro(form.purchasePrice) : null}
                onChange={value => set("purchasePrice", euroToCents(value))}
                disabled={!canEdit}
              />
            </Field>
            <Field label={t("Article discount (%, empty = discount group)")}>
              <InputNumber
                {...money}
                className="mat-full"
                min={-1000}
                max={100}
                value={
                  form.discount === null || form.discount === undefined
                    ? null
                    : bpToPercent(form.discount)
                }
                onChange={value =>
                  set("discount", value === null || value === undefined ? null : percentToBp(value))
                }
                disabled={!canEdit}
              />
            </Field>
          </>
        ) : null}
        <Field
          label={
            form.kind === "labour"
              ? t("Time per unit (minutes)")
              : t("Installation time per unit (minutes)")
          }
        >
          <InputNumber
            {...money}
            className="mat-full"
            min={0}
            precision={2}
            value={form.labour?.minutes || 0}
            onChange={value => setLabour("minutes", value || 0)}
            disabled={!canEdit}
          />
        </Field>
        <Field label={t("Wage group")}>
          <Select
            className="mat-full"
            value={form.labour?.wageGroup || settings.defaultWageGroup}
            options={wageOptions}
            onChange={value => setLabour("wageGroup", value)}
            disabled={!canEdit}
          />
        </Field>
        <Field label={t("Fixed sales price (€, empty = calculated)")}>
          <InputNumber
            {...money}
            className="mat-full"
            min={0}
            precision={2}
            value={form.fixedPrice ? centsToEuro(form.fixedPrice) : null}
            onChange={value => set("fixedPrice", euroToCents(value))}
            disabled={!canEdit}
          />
        </Field>
        <Field label={t("Active")}>
          <Switch
            checked={form.active}
            onChange={value => set("active", value)}
            disabled={!canEdit}
          />
        </Field>
        <Field label={t("Long text")} span={24}>
          <Input.TextArea
            autoSize={{ minRows: 3, maxRows: 10 }}
            value={form.longText}
            onChange={e => set("longText", e.target.value)}
            disabled={!canEdit}
          />
        </Field>
      </Row>

      {form.kind === "assembly" ? (
        <>
          <Divider orientation="left">{t("Parts")}</Divider>
          <List
            dataSource={form.components || []}
            locale={{ emptyText: <Empty description={t("No parts yet")} /> }}
            renderItem={(c, index) => (
              <List.Item
                actions={
                  canEdit
                    ? [
                        <Button
                          key="remove"
                          type="link"
                          danger
                          onClick={() =>
                            set(
                              "components",
                              form.components.filter((_, i) => i !== index)
                            )
                          }
                        >
                          {t("Remove")}
                        </Button>
                      ]
                    : []
                }
              >
                <Space wrap className="mat-full">
                  <InputNumber
                    {...money}
                    min={0.001}
                    value={c.quantity}
                    onChange={value =>
                      set(
                        "components",
                        form.components.map((x, i) =>
                          i === index ? { ...x, quantity: value || 0 } : x
                        )
                      )
                    }
                    disabled={!canEdit}
                  />
                  <span className="mat-wrap">
                    {c.part?.unit || ""} {c.part?.name || t("Unknown article")}
                  </span>
                </Space>
              </List.Item>
            )}
          />
          {canEdit ? (
            <PartSearch
              exclude={articleId}
              onPick={part =>
                set("components", [
                  ...(form.components || []),
                  { article: part._id, quantity: 1, part: { name: part.name, unit: part.unit } }
                ])
              }
            />
          ) : null}
        </>
      ) : null}

      <Divider orientation="left">{t("Calculation")}</Divider>
      {preview ? (
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label={t("Material cost per unit")}>
            {formatMillicents(preview.calc.materialCost, lng)}
          </Descriptions.Item>
          <Descriptions.Item label={t("Material markup")}>
            {bpToPercent(preview.calc.markup)} %
          </Descriptions.Item>
          <Descriptions.Item label={t("Labour")}>
            {preview.calc.minutes} min × {formatEuro(preview.calc.rate, lng)}/h
          </Descriptions.Item>
          <Descriptions.Item label={t("Cost per unit")}>
            {formatEuro(preview.cost, lng)}
          </Descriptions.Item>
          <Descriptions.Item label={t("Sales price per unit")}>
            <strong>{formatEuro(preview.price, lng)}</strong>
          </Descriptions.Item>
        </Descriptions>
      ) : (
        <Typography.Paragraph>
          {article
            ? `${t("Sales price per unit")}: ${formatEuro(article.unitPrice, lng)} · ${t("Cost per unit")}: ${formatEuro(article.unitCost, lng)}`
            : t("The price of an assembly is calculated from its parts when you save.")}
        </Typography.Paragraph>
      )}

      {!isNew && form.kind === "article" ? (
        <>
          <Divider orientation="left">{t("Stock")}</Divider>
          {stock.length ? (
            <List
              dataSource={stock}
              renderItem={item => (
                <List.Item>
                  <span className="mat-wrap">{item.itemName}</span>
                  <span className="mat-number">
                    {t("In stock")}: {Number(item.total || 0) - Number(item.used || 0)} {form.unit}
                  </span>
                </List.Item>
              )}
            />
          ) : meta.permissions.update ? (
            <Button onClick={createStock}>{t("Keep this article in stock")}</Button>
          ) : (
            <Typography.Text className="mat-muted">{t("Not kept in stock")}</Typography.Text>
          )}
          {meta.permissions.create ? (
            <Space wrap className="mat-section">
              <Select
                allowClear
                placeholder={t("Project")}
                popupMatchSelectWidth={false}
                value={booking.project}
                onFocus={() =>
                  projects.length ||
                  listProjects()
                    .then(res => setProjects(res.data.data))
                    .catch(generalErrorAlert)
                }
                onChange={value => setBooking(prev => ({ ...prev, project: value || null }))}
                options={projects.map(p => ({ value: p._id, label: p.projectName }))}
              />
              <InputNumber
                {...money}
                min={0.001}
                value={booking.quantity}
                onChange={value => setBooking(prev => ({ ...prev, quantity: value || 0 }))}
                addonAfter={form.unit}
              />
              <Button onClick={book} disabled={!booking.quantity}>
                {t("Book onto project")}
              </Button>
            </Space>
          ) : null}
        </>
      ) : null}
    </Drawer>
  );
};

export default ArticleEditor;
